import React from 'react';
import { useCurrentCalendar } from '../contexts/CurrentCalendarContext';
import {
  useBreakpoint,
  useWhichBreakpoint,
} from '../hooks/useBreakpoint';
import { useCalendarPage } from '../contexts/CalendarPageContext';
import { isToday } from '../lib/dateUtils';
import { shortenHolidayName } from '../lib/holidayUtils';
import MobileDayView from './MobileDayView';
import { useQueryClient } from '@tanstack/react-query';
import {
  EVENT_BAR_H,
  EVENT_BAR_GAP,
  EVENT_BAR_RADIUS,
  EVENT_PILL_H,
  EVENT_PILL_RADIUS,
  EVENT_BAR_FONT_SIZE,
  EVENT_PILL_FONT_SIZE,
  EVENT_LABEL_FONT_WEIGHT,
} from '../lib/eventBarStyles';
import { contrastText } from '../lib/calendarTheme';

const CIRCLE_ROW_H = 28;

// ── Tweak date-circle outlines ──
const OTHER_CIRCLE_OUTLINE_OPACITY = 0.55; // 0 = none, 1 = full accent color

// ── Tweak the header band borders (accent color, independent top/bottom) ──
const HEADER_BORDER_TOP_WIDTH = '1px'; // e.g. '0px', '1px', '3px'
const HEADER_BORDER_TOP_STYLE = 'solid'; // e.g. 'solid', 'dashed', 'dotted'
const HEADER_BORDER_BOT_WIDTH = '1px'; // e.g. '1px', '2px', '4px'
const HEADER_BORDER_BOT_STYLE = 'dotted'; // e.g. 'solid', 'dashed', 'dotted'

type TimeSlot = 'morning' | 'afternoon' | 'evening';

function getTimeSlot(startAt: string): TimeSlot {
  const hour = new Date(startAt).getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

const SLOT_ICONS: Record<TimeSlot, React.ReactNode> = {
  morning: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      className="w-3 h-3"
    >
      {/* horizon line */}
      <line x1="2" y1="17" x2="22" y2="17" />
      {/* sun half-circle rising above horizon */}
      <path d="M5 17a7 7 0 0 1 14 0" />
      {/* rays */}
      <line x1="12" y1="4" x2="12" y2="6" />
      <line x1="4.93" y1="6.93" x2="6.34" y2="8.34" />
      <line x1="19.07" y1="6.93" x2="17.66" y2="8.34" />
      <line x1="2" y1="13" x2="4" y2="13" />
      <line x1="20" y1="13" x2="22" y2="13" />
    </svg>
  ),
  afternoon: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-3 h-3"
    >
      <circle
        cx="12"
        cy="12"
        r="5"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  evening: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-3 h-3"
    >
      <path
        d="M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

const MAX_SQUARES_PER_SLOT = 4;

function getHolidayMaxLength(
  bp: ReturnType<typeof useWhichBreakpoint>
): number {
  // Desktop: no shortening needed
  if (
    bp === 'sm' ||
    bp === 'tablet' ||
    bp === 'lg' ||
    bp === 'xl' ||
    bp === '2xl'
  ) {
    return 100; // Large enough to not trigger shortening
  }
  // Mobile breakpoints: progressively shorter
  if (bp === 'miniscule') return 8;
  return 12; // default for unknown small sizes
}

export default function CalendarGrid() {
  const isDesktop = useBreakpoint('sm');
  const breakpoint = useWhichBreakpoint();
  const { theme } = useCurrentCalendar();
  const queryClient = useQueryClient();
  const holidayMaxLength = getHolidayMaxLength(breakpoint);

  const {
    calendarId,
    currentMonth,
    leadingBlanks,
    totalDays,
    calendarColorMap,
    calColor,
    handleDayClick,
    eventsOnDay,
    eventsOnOverflowDate,
    navigateToOverflowDate,
    multiDaySegments,
    expandedDay,
    holidaysByDay,
  } = useCalendarPage();

  const totalCells = leadingBlanks + totalDays;
  const numRows = Math.ceil(totalCells / 7);

  return (
    <div
      className="rounded-sm overflow-hidden shadow-page border"
      style={{ borderColor: theme.border }}
    >
      {Array.from({ length: numRows }).map((_, rowIdx) => {
        const rowSegments = multiDaySegments.filter(
          s => s.rowIndex === rowIdx
        );

        // Collect all single-day all-day events for this row, keyed by colIdx
        const allDayByCol: Map<
          number,
          (typeof rowSegments)[0]['event'][]
        > = new Map();
        // Columns in this row whose date falls outside the current month
        // (leading/trailing overflow), rendered faded and sourced from the
        // adjacent month's events.
        const overflowCols = new Set<number>();
        for (let colIdx = 0; colIdx < 7; colIdx++) {
          const gridPos = rowIdx * 7 + colIdx;
          const dayNumber = gridPos - leadingBlanks + 1;
          const isOverflow =
            dayNumber < 1 || dayNumber > totalDays;
          if (isOverflow) overflowCols.add(colIdx);
          const MS_PER_DAY = 86400000;
          const dayEvents = isOverflow
            ? eventsOnOverflowDate(
                new Date(
                  currentMonth.getFullYear(),
                  currentMonth.getMonth(),
                  dayNumber
                )
              )
            : eventsOnDay(dayNumber);
          const singleAllDay = dayEvents.filter(e => {
            if (!e.all_day) return false;
            if (e.start_date && e.end_date) {
              return e.start_date === e.end_date;
            }
            return (
              new Date(e.end_at).getTime() -
                new Date(e.start_at).getTime() <=
              MS_PER_DAY
            );
          });
          if (singleAllDay.length > 0) {
            allDayByCol.set(colIdx, singleAllDay);
          }
        }

        // Compute max stack depth for header band height
        // Single-day all-days occupy colStart=colIdx, colSpan=1, need their own stack slots
        // We merge them with rowSegments for a unified stack count
        const maxMultiDayStack =
          rowSegments.length > 0
            ? Math.max(
                ...rowSegments.map(s => s.stackIndex)
              ) + 1
            : 0;
        // Single-day all-days stack after multi-day bars, per column
        const maxSingleDayStack = Math.max(
          0,
          ...[...allDayByCol.values()].map(
            evs => evs.length
          )
        );
        const totalBarRows =
          maxMultiDayStack + maxSingleDayStack;
        // Header is one unified row: circles + bars share the same vertical space.
        // Height = enough to fit all stacked bars starting at BAR_TOP (4px), plus 2px bottom gap.
        const BAR_TOP = 4;
        const headerBandH = Math.max(
          CIRCLE_ROW_H,
          BAR_TOP +
            totalBarRows * (EVENT_BAR_H + EVENT_BAR_GAP) +
            2
        );

        return (
          <div
            key={`row-${rowIdx}`}
            style={{
              borderBottom: `1px solid ${theme.border}`,
            }}
          >
            {/* ── HEADER BAND: circles + all-day/multi-day bars inline ── */}
            <div
              className="relative"
              style={{
                height: headerBandH,
                borderTop: `${HEADER_BORDER_TOP_WIDTH} ${HEADER_BORDER_TOP_STYLE} ${calColor}`,
                borderBottom: `${HEADER_BORDER_BOT_WIDTH} ${HEADER_BORDER_BOT_STYLE} ${calColor}`,
              }}
            >
              {/* Column borders + background for blank cells, and date circles */}
              <div className="grid grid-cols-7 h-full">
                {Array.from({ length: 7 }).map(
                  (_, colIdx) => {
                    const gridPos = rowIdx * 7 + colIdx;
                    const dayNumber =
                      gridPos - leadingBlanks + 1;
                    const isOverflow =
                      dayNumber < 1 ||
                      dayNumber > totalDays;
                    const cellDate = isOverflow
                      ? new Date(
                          currentMonth.getFullYear(),
                          currentMonth.getMonth(),
                          dayNumber
                        )
                      : null;
                    const displayDayNumber = cellDate
                      ? cellDate.getDate()
                      : dayNumber;
                    const todayCell = cellDate
                      ? isToday(
                          cellDate,
                          cellDate.getDate()
                        )
                      : isToday(currentMonth, dayNumber);
                    return (
                      <div
                        key={`circle-${colIdx}`}
                        className="relative h-full"
                        style={{
                          borderRight: `1px solid ${theme.border}`,
                          backgroundColor: isOverflow
                            ? theme.surface2
                            : theme.surface3,
                        }}
                      >
                        <span
                          className="absolute top-1 left-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold font-mono leading-none select-none z-10"
                          style={{
                            opacity: isOverflow ? 0.45 : 1,
                            ...(todayCell
                              ? {
                                  backgroundColor: calColor,
                                  color: theme.contrastText,
                                  outline:
                                    '1px solid #1a1a1a',
                                  outlineOffset: '0px',
                                }
                              : {
                                  backgroundColor:
                                    theme.dateBg,
                                  color: theme.inkTint,
                                  boxShadow: `0 0 0 1.5px ${calColor}${Math.round(
                                    OTHER_CIRCLE_OUTLINE_OPACITY *
                                      255
                                  )
                                    .toString(16)
                                    .padStart(2, '0')}`,
                                }),
                          }}
                        >
                          {displayDayNumber}
                        </span>
                      </div>
                    );
                  }
                )}
              </div>

              {/* Multi-day bars — absolutely positioned spanning columns */}
              {rowSegments.map(seg => {
                const colWidthPct = 100 / 7;
                const leftPct = seg.colStart * colWidthPct;
                const widthPct = seg.colSpan * colWidthPct;
                const topPx =
                  BAR_TOP +
                  seg.stackIndex *
                    (EVENT_BAR_H + EVENT_BAR_GAP);
                const borderRadius =
                  seg.isStart && seg.isEnd
                    ? `${EVENT_BAR_RADIUS}px`
                    : seg.isStart
                      ? `${EVENT_BAR_RADIUS}px 0 0 ${EVENT_BAR_RADIUS}px`
                      : seg.isEnd
                        ? `0 ${EVENT_BAR_RADIUS}px ${EVENT_BAR_RADIUS}px 0`
                        : '0';
                return (
                  <div
                    key={`seg-${seg.event.id}-${seg.rowIndex}`}
                    className="absolute pointer-events-none flex items-center overflow-hidden"
                    style={{
                      top: topPx,
                      left: `calc(${leftPct}% + ${seg.isStart ? 2 : 0}px)`,
                      width: `calc(${widthPct}% - ${(seg.isStart ? 2 : 0) + (seg.isEnd ? 2 : 0)}px)`,
                      height: EVENT_BAR_H,
                      backgroundColor: seg.color,
                      borderRadius,
                      zIndex: 2,
                    }}
                    title={seg.event.title}
                  >
                    <span
                      className="truncate pl-7 pr-2"
                      style={{
                        color: contrastText(seg.color),
                        fontSize: EVENT_BAR_FONT_SIZE,
                        fontWeight: EVENT_LABEL_FONT_WEIGHT,
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {seg.event.title}
                    </span>
                  </div>
                );
              })}

              {/* Single-day all-day bars — one per cell column, stacked after multi-day */}
              {[...allDayByCol.entries()].flatMap(
                ([colIdx, evs]) =>
                  evs.map((ev, i) => {
                    const colWidthPct = 100 / 7;
                    const leftPct = colIdx * colWidthPct;
                    const topPx =
                      BAR_TOP +
                      (maxMultiDayStack + i) *
                        (EVENT_BAR_H + EVENT_BAR_GAP);
                    const color =
                      (
                        ev as {
                          subscription_color?: string;
                        }
                      ).subscription_color ??
                      calendarColorMap[ev.calendar_id] ??
                      calColor;
                    const isOverflowCol =
                      overflowCols.has(colIdx);
                    return (
                      <div
                        key={`allday-${ev.id}`}
                        className="absolute pointer-events-none flex items-center overflow-hidden"
                        style={{
                          top: topPx,
                          left: `calc(${leftPct}% + 2px)`,
                          width: `calc(${colWidthPct}% - 4px)`,
                          height: EVENT_BAR_H,
                          backgroundColor: color,
                          borderRadius: EVENT_BAR_RADIUS,
                          opacity: isOverflowCol ? 0.5 : 1,
                          zIndex: 2,
                        }}
                        title={ev.title}
                      >
                        <span
                          className="truncate pl-7 pr-2"
                          style={{
                            color: contrastText(color),
                            fontSize: EVENT_BAR_FONT_SIZE,
                            fontWeight:
                              EVENT_LABEL_FONT_WEIGHT,
                            fontFamily: 'var(--font-mono)',
                          }}
                        >
                          {ev.title}
                        </span>
                      </div>
                    );
                  })
              )}
            </div>

            {/* ── CELL BODIES: timed slot events only ── */}
            <div className="grid grid-cols-7">
              {Array.from({ length: 7 }).map(
                (_, colIdx) => {
                  const gridPos = rowIdx * 7 + colIdx;
                  const dayNumber =
                    gridPos - leadingBlanks + 1;
                  const isOverflow =
                    dayNumber < 1 || dayNumber > totalDays;
                  const cellDate = isOverflow
                    ? new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth(),
                        dayNumber
                      )
                    : null;
                  const day = cellDate
                    ? cellDate.getDate()
                    : dayNumber;
                  const MS_PER_DAY = 86400000;
                  const dayEvents = cellDate
                    ? eventsOnOverflowDate(cellDate)
                    : eventsOnDay(day);
                  const timedEvents = dayEvents.filter(
                    e =>
                      !e.all_day &&
                      new Date(e.end_at).getTime() -
                        new Date(e.start_at).getTime() <=
                        MS_PER_DAY
                  );

                  return (
                    <div
                      key={`cell-${gridPos}`}
                      data-testid="calendar-day-cell"
                      data-overflow-day={
                        isOverflow ? 'true' : undefined
                      }
                      onClick={() =>
                        cellDate
                          ? navigateToOverflowDate(cellDate)
                          : handleDayClick(day)
                      }
                      className="min-h-20 group cursor-pointer active:brightness-95 transition-colors flex flex-col"
                      style={{
                        backgroundColor: isOverflow
                          ? theme.surface2
                          : theme.surface1,
                        borderRight: `1px solid ${theme.border}`,
                        opacity: isOverflow ? 0.55 : 1,
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor =
                          isOverflow
                            ? theme.surface3
                            : theme.surface2;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor =
                          isOverflow
                            ? theme.surface2
                            : theme.surface1;
                      }}
                    >
                      {/* Time-slot zones */}
                      {(() => {
                        const slotCounts: Record<
                          TimeSlot,
                          number
                        > = {
                          morning: 0,
                          afternoon: 0,
                          evening: 0,
                        };
                        for (const ev of timedEvents) {
                          slotCounts[
                            getTimeSlot(ev.start_at)
                          ]++;
                        }
                        const hasBusySlot = Object.values(
                          slotCounts
                        ).some(c => c >= 3);
                        const collapsed = new Set(
                          (
                            [
                              'morning',
                              'afternoon',
                              'evening',
                            ] as TimeSlot[]
                          ).filter(
                            slot =>
                              slotCounts[slot] === 0 &&
                              hasBusySlot
                          )
                        );

                        return (
                          [
                            'morning',
                            'afternoon',
                            'evening',
                          ] as TimeSlot[]
                        ).map(slot => {
                          const slotEvents =
                            timedEvents.filter(
                              ev =>
                                getTimeSlot(ev.start_at) ===
                                slot
                            );
                          const visible = slotEvents.slice(
                            0,
                            MAX_SQUARES_PER_SLOT
                          );
                          const overflow =
                            slotEvents.length -
                            visible.length;
                          const isCollapsed =
                            collapsed.has(slot);
                          return (
                            <div
                              key={slot}
                              data-testid={`time-slot-${slot}`}
                              className={
                                isCollapsed
                                  ? 'flex-none h-2 flex flex-row items-end pb-0.5 gap-px'
                                  : 'flex flex-row items-end pb-0.5 gap-px'
                              }
                              style={
                                isCollapsed
                                  ? undefined
                                  : {
                                      flex: Math.min(
                                        Math.max(
                                          slotEvents.length,
                                          1
                                        ),
                                        3
                                      ),
                                    }
                              }
                            >
                              {/* Slot icon — left column */}
                              {!isCollapsed && (
                                <span
                                  className="w-4 shrink-0 flex items-end justify-center pb-px pointer-events-none"
                                  style={{
                                    opacity: 0.6,
                                    color: theme.inkTint,
                                  }}
                                >
                                  {SLOT_ICONS[slot]}
                                </span>
                              )}
                              {/* Event pills */}
                              <div
                                className={
                                  isCollapsed
                                    ? 'flex-1 flex items-end gap-px min-w-0'
                                    : 'flex-1 flex flex-wrap items-end content-end gap-px min-w-0'
                                }
                              >
                                {visible.map(ev => {
                                  const color =
                                    (
                                      ev as {
                                        subscription_color?: string;
                                      }
                                    ).subscription_color ??
                                    calendarColorMap[
                                      ev.calendar_id
                                    ] ??
                                    calColor;
                                  return (
                                    <span
                                      key={ev.id}
                                      className="flex items-center px-0.5 overflow-hidden"
                                      style={{
                                        flex: '1 1 50%',
                                        backgroundColor:
                                          color,
                                        height:
                                          EVENT_PILL_H,
                                        borderRadius:
                                          EVENT_PILL_RADIUS,
                                      }}
                                      title={ev.title}
                                    >
                                      <span
                                        className="truncate leading-none hidden [&:not(:empty)]:block"
                                        style={{
                                          minWidth: 0,
                                          color:
                                            contrastText(
                                              color
                                            ),
                                          fontSize:
                                            EVENT_PILL_FONT_SIZE,
                                          fontWeight:
                                            EVENT_LABEL_FONT_WEIGHT,
                                          fontFamily:
                                            'var(--font-mono)',
                                        }}
                                      >
                                        {ev.title}
                                      </span>
                                    </span>
                                  );
                                })}
                                {overflow > 0 && (
                                  <span className="text-[7px] font-mono leading-none self-center text-gray-400 shrink-0">
                                    +{overflow}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}

                      {/* Holidays — subtle text at bottom (not shown for
                          adjacent-month overflow days) */}
                      {(() => {
                        if (isOverflow) return null;
                        const holidays =
                          holidaysByDay.get(day);
                        if (
                          !holidays ||
                          holidays.length === 0
                        )
                          return null;
                        return (
                          <div className="mt-auto px-1.5 pb-1 pt-0.5">
                            {holidays.map((name, idx) => (
                              <span
                                key={idx}
                                className="block text-[10px] font-mono text-gray-400 truncate"
                                title={name}
                              >
                                {shortenHolidayName(
                                  name,
                                  holidayMaxLength
                                )}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        );
      })}

      {/* Mobile day view overlay */}
      {!isDesktop && expandedDay && (
        <MobileDayView
          selectedDate={expandedDay}
          onEventCreated={() => {
            queryClient.invalidateQueries({
              queryKey: ['events', calendarId],
            });
          }}
        />
      )}
    </div>
  );
}
